import storage from 'redux-persist/lib/storage';
import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { persistReducer } from 'redux-persist';
import type { AxiosResponse } from 'axios';
import { api } from 'libs';
import initialState from 'state/initial-state';
import schema from 'state/schema';
import serializeRequestError from 'state/utils/serialize-request-error';
import { analytics } from 'libs';
import type { AppDispatch, AppGetState } from './index';

type BrokerageSetting = { key: string; value: number };
type SettingsByKey = Record<string, BrokerageSetting>;

/**
 * Hydrate Initial State global LS object.
 * @type {Object}
 */
let initialByBrokerageIdState: Record<string, SettingsByKey> = {};
let initialByIdState = {};
const initialForUserState: { useTableView: boolean } & Record<string, boolean> = {
  useTableView: false,
};
const hydratedSettings = initialState.data?.settings;
const hydratedBrokerages = initialState.data?.brokerages;

if (hydratedSettings) {
  const data = normalize(hydratedSettings, [schema.setting]);
  initialByIdState = data.entities.settings || {};
}

if (hydratedBrokerages) {
  initialByBrokerageIdState = hydratedBrokerages.reduce(
    (accum: Record<string, SettingsByKey>, brokerage: { id: string | number }) => {
      const payload = normalize(brokerage, schema.brokerage);
      return {
        ...accum,
        [brokerage.id]: payload.entities.settings || {},
      };
    },
    {}
  );
}

const byIdSlice = createSlice({
  name: 'lenderspotlight/settings/byId',
  initialState: initialByIdState,
  reducers: {
    addForBrokerage: (
      _state,
      _action: PayloadAction<{ brokerageId: any; key: string; setting: any; value: number }>
    ) => {},
    addForBrokerageFailed: (
      _state,
      _action: PayloadAction<{ brokerageId: any; key: string; setting: any; value: number }>
    ) => {},
    fetchStarted: () => {},
    fetchForBrokerageStarted: () => {},
    fetchForBrokerageFailed: () => {},
    fetchForBrokerageSucceeded: (
      _state,
      _action: PayloadAction<{ brokerageId: any; settings: Record<string, any> }>
    ) => {},
    fetchFailed: () => {},
    fetchSucceeded: (state, action) => ({
      ...state,
      ...(action.payload.entities?.settings || {}),
    }),
    removeForBrokerage: (
      _state,
      _action: PayloadAction<{ brokerageId: any; key: string; setting: any; value: number }>
    ) => {},
    removeForBrokerageFailed: (
      _state,
      _action: PayloadAction<{ brokerageId: any; key: string; setting: any; value: number }>
    ) => {},
    updateSetting: (_state, _action: PayloadAction<{ key: string; value: any }>) => {},
  },
});

const {
  addForBrokerage,
  addForBrokerageFailed,
  fetchFailed,
  fetchForBrokerageFailed,
  fetchForBrokerageStarted,
  fetchForBrokerageSucceeded,
  fetchStarted,
  fetchSucceeded,
  removeForBrokerage,
  removeForBrokerageFailed,
  updateSetting,
} = byIdSlice.actions;

const byBrokerageIdSlice = createSlice({
  name: 'lenderspotlight/settings/byBrokerageId',
  initialState: initialByBrokerageIdState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchForBrokerageSucceeded, (state, action) => {
      state[action.payload.brokerageId] = action.payload.settings;
    });

    builder.addCase(addForBrokerage, (state, action) => {
      const { brokerageId, key, value } = action.payload;
      state[brokerageId] = {
        ...(state[brokerageId] || {}),
        [key]: {
          key,
          value,
        },
      };
    });

    builder.addCase(removeForBrokerage, (state, action) => {
      const { brokerageId, key, value } = action.payload;
      state[brokerageId] = {
        ...(state[brokerageId] || {}),
        [key]: {
          key,
          value,
        },
      };
    });

    builder.addCase(addForBrokerageFailed, (state, action) => {
      const { brokerageId, key, setting } = action.payload;
      if (!state[brokerageId]) {
        state[brokerageId] = {};
      }

      if (setting) {
        state[brokerageId][key] = setting;
      } else {
        delete state[brokerageId][key];
      }
    });

    builder.addCase(removeForBrokerageFailed, (state, action) => {
      const { brokerageId, key, setting } = action.payload;
      if (!state[brokerageId]) {
        state[brokerageId] = {};
      }

      if (setting) {
        state[brokerageId][key] = setting;
      } else {
        delete state[brokerageId][key];
      }
    });
  },
});

const pendingRequestsCountSlice = createSlice({
  name: 'lenderspotlight/settings/pendingRequestsCount',
  initialState: 0,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchStarted, (state) => state + 1);
    builder.addCase(fetchForBrokerageStarted, (state) => state + 1);
    builder.addCase(fetchFailed, (state) => state - 1);
    builder.addCase(fetchSucceeded, (state) => state - 1);
    builder.addCase(fetchForBrokerageFailed, (state) => state - 1);
    builder.addCase(fetchForBrokerageSucceeded, (state) => state - 1);
  },
});

const forUserSlice = createSlice({
  name: 'lenderspotlight/settings/forUser',
  initialState: initialForUserState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(updateSetting, (state, action) => {
      state[action.payload.key] = action.payload.value;
    });
  },
});

/**
 * Persistor Configuration.
 * @type {Object}
 */
const persistConfig = {
  key: 'forUser',
  storage,
};

/**
 * Export Lender Reducer
 * @type {Object}
 */
export default combineReducers({
  byId: byIdSlice.reducer,
  byBrokerageId: byBrokerageIdSlice.reducer,
  forUser: persistReducer(persistConfig, forUserSlice.reducer),
  pendingRequestsCount: pendingRequestsCountSlice.reducer,
});

/**
 * Fetch all settings success action creator.
 * @param  {Object} response
 * @return {Object}
 */
const fetchAllSuccess = (response: AxiosResponse) => fetchSucceeded(normalize(response.data, [schema.setting]));

/**
 * Fetch brokerage success action creator.
 * @param  {Object} response
 * @param  {String} brokerageId
 * @return {Object}
 */
const fetchByBrokerageSuccess = (response: AxiosResponse, brokerageId: string | number) => {
  const payload = normalize(response.data, [schema.setting]);
  return fetchForBrokerageSucceeded({
    brokerageId,
    settings: payload.entities.settings || {},
  });
};

/**
 * Fetch all settings
 * @return {Function}
 */
export const fetchAll = () => async (dispatch: AppDispatch) => {
  dispatch(fetchStarted());

  try {
    const response = await api.get('settings');
    return dispatch(fetchAllSuccess(response));
  } catch (error) {
    return dispatch({ type: fetchFailed.type, error: serializeRequestError(error) });
  }
};

/**
 * Fetch the settings for the current brokerage.
 * @param {Number} brokerageId
 * @return {Function}
 */
export const fetchByBrokerageId = (brokerageId: string | number) => async (dispatch: AppDispatch) => {
  dispatch(fetchForBrokerageStarted());

  try {
    const response = await api.get(`brokerages/${brokerageId}/settings`);
    return dispatch(fetchByBrokerageSuccess(response, brokerageId));
  } catch (error) {
    return dispatch({ type: fetchForBrokerageFailed.type, error: serializeRequestError(error) });
  }
};

/**
 * Update the persisted user setting.
 * @param  {String} key
 * @param  {String} value
 * @return {Function}
 */
export const update = (key: string, value: boolean) => {
  if (key === 'useTableView') {
    analytics.productSearch.layoutView(value ? 'table' : 'card');
  }

  return (dispatch: AppDispatch) =>
    dispatch(
      updateSetting({
        key,
        value,
      })
    );
};

/**
 * Add a setting by key
 * @param {Integer} brokerageId
 * @param {String} key
 * @return {Function}
 */
const addBrokerageSetting =
  (brokerageId: string | number, key: string, value = 1) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const setting = getState().settings.byBrokerageId[brokerageId]?.[key];
    const payload = { brokerageId, key, setting, value };
    dispatch(addForBrokerage(payload));

    try {
      return await api.post(`brokerages/${brokerageId}/settings`, { key });
    } catch (error) {
      return dispatch({
        type: addForBrokerageFailed.type,
        error: serializeRequestError(error),
        payload,
      });
    }
  };

/**
 * Remove a setting by key
 * @param {Integer} brokerageId
 * @param {String} key
 * @return {Function}
 */
const removeBrokerageSetting =
  (brokerageId: string | number, key: string, value = 0) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    const setting = getState().settings.byBrokerageId[brokerageId]?.[key];
    const payload = { brokerageId, key, setting, value };
    dispatch(removeForBrokerage(payload));

    try {
      return await api.delete(`brokerages/${brokerageId}/settings`, { data: { key } });
    } catch (error) {
      return dispatch({
        type: removeForBrokerageFailed.type,
        error: serializeRequestError(error),
        payload,
      });
    }
  };

/**
 * Toggle a setting by key
 * @param {Integer} brokerageId
 * @param {String} key
 * @return {Function}
 */
export const toggleBrokerageSetting =
  (brokerageId: string | number, key: string) => (dispatch: AppDispatch, getState: AppGetState) => {
    const settings = getState().settings.byBrokerageId[brokerageId] || {};
    const isActive = Object.keys(settings)
      .map((settingId) => settings[settingId])
      .reduce((accum, setting) => (setting.key === key && setting.value >= 1) || accum, false);

    return isActive
      ? removeBrokerageSetting(brokerageId, key)(dispatch, getState)
      : addBrokerageSetting(brokerageId, key)(dispatch, getState);
  };
